// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Slider from '@mui/material/Slider'
import Typography from '@mui/material/Typography'
import { erc20WeiToErc20DecString } from '@shared/conversions'
import React, { useState } from 'react'
import { LytReservesClass } from './LytReservesClass'
import { ReserveActionPlanClass, ReservePlanModeEnum } from './ReserveActionPlanClass'

export default function PlanAdjuster(props: {
  reserveActionPlanClass: ReserveActionPlanClass
  doRedraw: () => void
}): React.ReactElement {
  const { reserveActionPlanClass } = props
  const reservesWithStats = reserveActionPlanClass.reservesWithStats
  const [reserve1, setReserve1] = useState<LytReservesClass | undefined>(undefined)
  const [reserve2, setReserve2] = useState<LytReservesClass | undefined>(undefined)
  const [adjustment, setAdjustment] = useState(0)
  const [step, setStep] = useState(100_000000)
  React.useEffect(() => {
    // subscribe on mount, unsubscribe on unmount
    return reserveActionPlanClass.subscribe(() => {
      const adjustingReserves = reservesWithStats.filter((reserve) => reserve.isAdjusting)
      const r1 = adjustingReserves?.length > 0 ? adjustingReserves[0] : undefined
      const r2 = adjustingReserves?.length > 1 ? adjustingReserves[1] : undefined
      setReserve1(r1)
      setReserve2(r2)
      if (reserveActionPlanClass.reservePlanMode === ReservePlanModeEnum.InflowMode) {
        setAdjustment(Number(r1?.actualDepositAmount ?? 0n))
      } else {
        setAdjustment(Number(r1?.actualWithdrawAmount ?? 0n))
      }
    })
  }, [reserveActionPlanClass])

  function updateAdjustment(value: number) {
    setAdjustment(value)
    reserveActionPlanClass.updateAdjustment(value)

    reserveActionPlanClass.validate()
    props.doRedraw()
  }

  function reserveValues(reserve?: LytReservesClass): React.ReactElement {
    if (!reserve) {
      return <Box sx={{ flex: 1 }}></Box>
    }
    if (reserveActionPlanClass.reservePlanMode === ReservePlanModeEnum.InflowMode) {
      return (
        <Box sx={{ flex: 1 }}>
          <Typography variant="body1">{reserve.poolName}</Typography>
          <Typography variant="body1">
            Actual Deposit: {erc20WeiToErc20DecString(reserve.actualDepositAmount)}
          </Typography>
          <Typography variant="body1">Allocation: {reserve.postPercentReserves.toFixed(2)}%</Typography>
          <Typography variant="body1">
            Default Deposit: {erc20WeiToErc20DecString(reserve.defaultDepositAmount)}
          </Typography>
          <Typography
            color={reserve.actualDepositAmount >= reserve.minBalanceAmount ? 'success' : 'error'}
            variant="body1"
          >
            Min Balance: {erc20WeiToErc20DecString(reserve.minBalanceAmount)}
          </Typography>
          <Typography
            color={reserve.actualDepositAmount <= reserve.maxBalanceAmount ? 'success' : 'error'}
            variant="body1"
          >
            Max Balance: {erc20WeiToErc20DecString(reserve.maxBalanceAmount)}
          </Typography>
        </Box>
      )
    }
    if (reserveActionPlanClass.reservePlanMode === ReservePlanModeEnum.RetrieveMode) {
      return (
        <Box sx={{ flex: 1 }}>
          <Typography variant="body1">{reserve.poolName}</Typography>
          <Typography variant="body1">
            Actual Withdraw: {erc20WeiToErc20DecString(reserve.actualWithdrawAmount)}
          </Typography>
          <Typography variant="body1">Allocation: {reserve.postPercentReserves.toFixed(2)}%</Typography>
          <Typography variant="body1">
            Default Withdraw: {erc20WeiToErc20DecString(reserve.defaultWithdrawAmount)}
          </Typography>
          <Typography
            color={reserve.actualWithdrawAmount >= reserve.minBalanceAmount ? 'success' : 'error'}
            variant="body1"
          >
            Min Balance: {erc20WeiToErc20DecString(reserve.minBalanceAmount)}
          </Typography>
          <Typography
            color={reserve.actualWithdrawAmount <= reserve.maxBalanceAmount ? 'success' : 'error'}
            variant="body1"
          >
            Max Balance: {erc20WeiToErc20DecString(reserve.maxBalanceAmount)}
          </Typography>
        </Box>
      )
    }

    return <> </>
  }
  const outOfBounds =
    (reserve1?.actualDepositAmount &&
      reserve1?.minBalanceAmount &&
      reserve1?.actualDepositAmount < reserve1?.minBalanceAmount) ||
    (reserve2?.actualDepositAmount &&
      reserve2?.minBalanceAmount &&
      reserve2?.actualDepositAmount < reserve2?.minBalanceAmount) ||
    (reserve1?.actualDepositAmount &&
      reserve1?.maxBalanceAmount &&
      reserve1?.actualDepositAmount > reserve1?.maxBalanceAmount) ||
    (reserve2?.actualDepositAmount &&
      reserve2?.maxBalanceAmount &&
      reserve2?.actualDepositAmount > reserve2?.maxBalanceAmount)

  if (
    reserveActionPlanClass.reservePlanMode === ReservePlanModeEnum.OutflowMode ||
    reserveActionPlanClass.reservePlanMode === ReservePlanModeEnum.NeutralMode
  ) {
    return <></>
  }
  return (
    <Paper>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2, my: 2 }}>
        <Typography variant="h3">Plan Adjuster</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, my: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Step Size:
          </Typography>
          <Select
            size="small"
            value={step}
            onChange={(event) => {
              setStep(Number(event.target.value))
            }}
          >
            <MenuItem value={100000}>0.1</MenuItem>
            <MenuItem value={1_000000}>1</MenuItem>
            <MenuItem value={100_000000}>100</MenuItem>
            <MenuItem value={1000_000000}>1000</MenuItem>
            <MenuItem value={10_000_000000}>10000</MenuItem>
          </Select>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2, my: 2 }}>
        <Box sx={{ flex: 1 }}>{reserveValues(reserve1)}</Box>

        <Box
          sx={{ display: 'flex', flex: 3, flexDirection: 'column', alignItems: 'center', gap: 2, my: 2, width: '100%' }}
        >
          <Typography variant="body2" color="text.secondary">
            Adjust Balance
          </Typography>
          <Slider
            color={outOfBounds ? 'error' : 'success'}
            value={adjustment}
            step={step}
            min={Number(reserveActionPlanClass.adjustmentMinValue)}
            max={Number(reserveActionPlanClass.adjustmentMaxValue)}
            valueLabelFormat={(value) => (value / 1000000).toFixed(2)}
            onChange={(_event, value) => {
              updateAdjustment(value as number)
            }}
            valueLabelDisplay="auto"
            sx={{ width: '100%' }}
          />
        </Box>

        <Box sx={{ flex: 1 }}>{reserveValues(reserve2)}</Box>
      </Box>
    </Paper>
  )
}
